/**
 * Sub-tenant scope: external user ids for `memberUserIds` / `member_user_ids`.
 *
 * Returns users returned by RBAC for that sub-account, merged with the org owner's external
 * user id. The owner isn't a member/access-grant row of any specific (sub-)account, but they
 * can act on any account in the org — so resources they personally created while switched into
 * a sub-account (e.g. campaigns) must still resolve via this aggregate list, or they become
 * invisible to anyone browsing that scope, including the owner themselves.
 *

 * RBAC list users. Parameter order matches the API:
 * `GET /api/accounts/{subTenantAccountId}/users?includesubaccounts=true&tenantid={orgId}`
 * e.g. sub-tenant **35**, org **4** → `/api/accounts/35/users?includesubaccounts=true&tenantid=4`
 * → `users[].external_user_id`, read directly from that response. Only falls back to
 * `GET /api/access/users/{user_id}` per row for members whose `external_user_id` wasn't present
 * in the list response (e.g. an older rbac-svc deploy) — avoids one request per member.
 *
 * `subTenantAccountId` is `selectedSubAccountId` (path). `tenantid` is **organization id** (`org_id` /
 * `userContext.organization_id`), not parent account id.
 *
 * Cache is optional; callers that switch sub-tenant should use bypassCache so membership is always fresh.
 */
import * as rbacApi from '@/services/rbacApi';
import type { AccountUserAccess, Organization } from '@/types/rbac';

const CACHE_VERSION = 3;
const CACHE_PREFIX = `boostt_subtenant_member_ext_ids_v${CACHE_VERSION}`;

/**
 * RBAC `currentOrgId` can be null on first paint; session user often already has org id.
 * `fetchSubTenantMemberUserIds` returns [] without org id → no `member_user_ids` on listings.
 */
export function resolveOrgIdForSubTenantMemberFetch(
  rbacOrgId: number | null | undefined,
  sessionUser: unknown
): number | null {
  const fromRbac = rbacOrgId != null ? Number(rbacOrgId) : NaN;
  if (Number.isFinite(fromRbac) && fromRbac > 0) return fromRbac;
  if (!sessionUser || typeof sessionUser !== 'object') return null;
  const u = sessionUser as Record<string, unknown>;
  const raw = u.organization_id ?? u.organizationId ?? u.organizationID ?? u.org_id;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

type CachePayload = {
  v: number;
  /** Sub-tenant id: first path segment `/api/accounts/{this}/users` */
  subTenantAccountId: number;
  /** Organization id: `tenantid` query param */
  orgId: number;
  tenantExternalUserIds: number[];
  savedAt: number;
};

const CACHE_TTL_MS = 15 * 60 * 1000;

function cacheKey(subTenantAccountId: number, orgId: number): string {
  return `${CACHE_PREFIX}:${subTenantAccountId}:${orgId}`;
}

function readCache(subTenantAccountId: number, orgId: number): number[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(cacheKey(subTenantAccountId, orgId));
    if (!raw) return null;
    const p = JSON.parse(raw) as CachePayload;
    if (
      p.v !== CACHE_VERSION ||
      p.subTenantAccountId !== subTenantAccountId ||
      p.orgId !== orgId ||
      !Array.isArray(p.tenantExternalUserIds)
    ) {
      return null;
    }
    if (Date.now() - (p.savedAt || 0) > CACHE_TTL_MS) {
      localStorage.removeItem(cacheKey(subTenantAccountId, orgId));
      return null;
    }
    return p.tenantExternalUserIds;
  } catch {
    return null;
  }
}

/**
 * Synchronous read of the persisted member-id cache (15-minute TTL), for callers that want to
 * render immediately with the last known ids and refresh in the background. null on a miss.
 */
export function readCachedSubTenantMemberUserIds(
  subTenantAccountId: number,
  orgId?: number | null,
): number[] | null {
  const orgIdNum = orgId != null ? Number(orgId) : NaN;
  if (!Number.isFinite(orgIdNum)) return null;
  return readCache(subTenantAccountId, orgIdNum);
}

function writeCache(
  subTenantAccountId: number,
  orgId: number,
  tenantExternalUserIds: number[]
): void {
  if (typeof window === 'undefined') return;
  try {
    const payload: CachePayload = {
      v: CACHE_VERSION,
      subTenantAccountId,
      orgId,
      tenantExternalUserIds,
      savedAt: Date.now(),
    };
    localStorage.setItem(cacheKey(subTenantAccountId, orgId), JSON.stringify(payload));
  } catch {
    /* noop */
  }
}

function rbacIdFromAccessRow(u: AccountUserAccess): number | null {
  const raw = u.user_id ?? u.userid;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function externalIdFromAccessRow(u: AccountUserAccess): number | null {
  // external_user_id is legitimately negative for a lot of real rows in this dataset —
  // only 0/missing (rbac-svc's normalizeMeContext "unset" sentinel) means "not present".
  const n = Number(u.external_user_id);
  return Number.isFinite(n) && n !== 0 ? n : null;
}

// Coalesces callers requesting the same (subTenantAccountId, orgId) within a short window: at
// least 11 independent dashboard widgets (DigitalAdAxios, QRCodeAxios, EmailAxios,
// PredictionAxios, DirectMailAxios, AudienceBuilderAxios, WorkflowAxios, useDashboardData,
// ChannelsTab, JourneysDashboard, journeys/details) each call fetchSubTenantMemberUserIds
// independently on mount, and nearly all of them pass bypassCache: true (so a real sub-tenant
// switch always gets fresh membership, not a stale 15-minute cache) — which also skips the
// persisted-cache check below. If React mounts them even slightly staggered (not the exact same
// tick), a plain "still in-flight" dedup misses most of them: request #1 has already settled by
// the time #2 starts, so #2 sees nothing to share and fires its own GET /api/accounts/{id}/users
// — a request stampede for identical data, repeated for every widget.
// Fix: keep a settled entry around for DEDUP_GRACE_MS before dropping it, so a burst of staggered
// mounts for the *same* account still collapses to one real request. This can't go stale across a
// genuine account switch — the map key includes subTenantAccountId, so switching to a different
// account is always a cache miss regardless of this grace period. Failed fetches are removed
// immediately (not kept), so a transient error doesn't get replayed to every other caller.
// 20s (rather than the original 3s) because some accounts have been observed taking 12-20s+ for
// the whole dashboard to settle (see documentation/Digital_Ads_and_RBAC_Dashboard_Latency_Handoff.md)
// — widgets there mount more than 3s apart while earlier requests are still queued/in flight, so a
// 3s grace window stopped coalescing them for the *same* (subTenantAccountId, orgId) pair.
const DEDUP_GRACE_MS = 20000;
const inFlightFetches = new Map<string, Promise<number[]>>();

// The GET /api/orgs/{id} dupes actually observed (see the same handoff doc, §5) turned out to be
// a *different* stampede than the one above: multiple *different* sub-tenant accounts under the
// same org (e.g. accounts 242, 199, 397, 430, 629 all under org 39) each run their own
// doFetchSubTenantMemberUserIds — a legitimate cache miss per account, since the member list genuinely
// differs — but each one also independently calls getOrganization(orgId) purely to read the org
// owner's external id, which is identical for every account in that org. The (subTenantAccountId,
// orgId) dedup key above can't catch this because subTenantAccountId differs per caller. Dedupe
// getOrganization itself, keyed on orgId alone, so N sub-accounts under one org still share a
// single /api/orgs/{id} call.
const ORG_DEDUP_GRACE_MS = 20000;
const orgFetches = new Map<number, Promise<Organization>>();

function getOrganizationDeduped(orgId: number): Promise<Organization> {
  const existing = orgFetches.get(orgId);
  if (existing) {
    return existing;
  }
  const promise = rbacApi.getOrganization(orgId);
  orgFetches.set(orgId, promise);
  promise.then(
    () => {
      setTimeout(() => {
        if (orgFetches.get(orgId) === promise) orgFetches.delete(orgId);
      }, ORG_DEDUP_GRACE_MS);
    },
    () => {
      if (orgFetches.get(orgId) === promise) orgFetches.delete(orgId);
    }
  );
  return promise;
}

/**
 * Returns **external** user IDs for the sub-tenant's RBAC members, merged with the org owner's
 * external user id (see module doc for why the owner must always be included).
 */
export async function fetchSubTenantMemberUserIds(
  subTenantAccountId: number,
  options?: {
    /** Organization id for RBAC `?tenantid=` (org_id) */
    orgId?: number | null;
    /** When true (e.g. after sub-tenant switch), always hit RBAC; ignore cache */
    bypassCache?: boolean;
  }
): Promise<number[]> {
  const orgIdRaw = options?.orgId != null ? Number(options.orgId) : NaN;
  const orgId = Number.isFinite(orgIdRaw) ? orgIdRaw : null;
  if (orgId == null) {
    return [];
  }

  if (!options?.bypassCache) {
    const hit = readCache(subTenantAccountId, orgId);
    if (hit !== null) {
      return [...hit];
    }
  }

  const key = cacheKey(subTenantAccountId, orgId);
  const existing = inFlightFetches.get(key);
  if (existing) {
    return [...(await existing)];
  }

  const promise = doFetchSubTenantMemberUserIds(subTenantAccountId, orgId);
  inFlightFetches.set(key, promise);
  promise.then(
    () => {
      // Keep the settled result shareable briefly so staggered (not exactly concurrent)
      // callers for the same account within this window still get one shared request.
      setTimeout(() => {
        if (inFlightFetches.get(key) === promise) inFlightFetches.delete(key);
      }, DEDUP_GRACE_MS);
    },
    () => {
      // Don't replay a failure to other callers — let the next one retry immediately.
      if (inFlightFetches.get(key) === promise) inFlightFetches.delete(key);
    }
  );
  return promise;
}

async function doFetchSubTenantMemberUserIds(
  subTenantAccountId: number,
  orgId: number
): Promise<number[]> {
  // rbacUserId -> external_user_id, when the list response already included it (no round-trip needed).
  const knownExternalIds = new Map<number, number>();
  let rbacIds: number[] = [];
  try {
    const resp = await rbacApi.listAccountUsers(subTenantAccountId, true, orgId, true);
    const rows = (resp.users || []) as AccountUserAccess[];
    for (const u of rows) {
      const id = rbacIdFromAccessRow(u);
      if (id == null) continue;
      rbacIds.push(id);
      const ext = externalIdFromAccessRow(u);
      if (ext != null) knownExternalIds.set(id, ext);
    }
  } catch {
    rbacIds = [];
  }

  rbacIds = Array.from(new Set(rbacIds));

  // Only fall back to a per-user GET /api/access/users/{id} for rows the list call didn't
  // already resolve — avoids one request per member (was previously unconditional per id).
  const unresolvedIds = rbacIds.filter((id) => !knownExternalIds.has(id));

  const fetchedExternalIds = await Promise.all(
    unresolvedIds.map(async (rbacUserId) => {
      try {
        const u = await rbacApi.getUser(rbacUserId);
        const ext = Number(u.external_user_id);
        if (Number.isFinite(ext) && ext > 0) return ext;
        const id = Number(u.id);
        return Number.isFinite(id) && id > 0 ? id : null;
      } catch {
        return null;
      }
    })
  );

  const externalFromRbac = [...Array.from(knownExternalIds.values()), ...fetchedExternalIds];

  let ownerExternalId: number | null = null;
  try {
    const org = await getOrganizationDeduped(orgId);
    const ownerId = Number(org?.owner_user_id);
    if (Number.isFinite(ownerId) && ownerId > 0) ownerExternalId = ownerId;
  } catch {
    ownerExternalId = null;
  }

  const tenantExternals = Array.from(
    new Set([
      ...externalFromRbac.filter((x): x is number => x != null),
      ...(ownerExternalId != null ? [ownerExternalId] : []),
    ])
  );

  // Written on bypassCache fetches too: bypass means "don't *read* a possibly-stale entry",
  // but the result is fresh, so it's the best value for the next reader (e.g. the dashboard's
  // instant first paint via readCachedSubTenantMemberUserIds).
  writeCache(subTenantAccountId, orgId, tenantExternals);

  return tenantExternals;
}

/** Clear cached member external ids for a sub-tenant scope (path id + org `tenantid`). */
export function clearSubTenantMemberUserCache(subTenantAccountId: number, orgId: number): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(cacheKey(subTenantAccountId, orgId));
  } catch {
    /* noop */
  }
}

/** Remove all cached sub-tenant member lists (e.g. after org structure change). */
export function clearAllSubTenantMemberUserCaches(): void {
  if (typeof window === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* noop */
  }
}
